import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  Dispatch,
  SetStateAction
} from 'react';
import styles from './EditLessonForm.module.scss';
import Button from '@components/button/Button';
import { isEmptyObject, validateIsEmpty } from '@utils/validation';
import { useHistory } from 'react-router-dom';
import {
  useGetUrlLazyQuery,
  useSllDeleteLessonMutation,
  useSllGetLessonsForSaLazyQuery,
  useIsValidSllUniqueNameLazyQuery,
  ContentType
} from '@api/graphql-codegen';
import Spinner from '@components/spinner/Spinner';
import EditLessonForm, { ValidationErrorKeys, validationErrors } from './EditLessonForm';
import { EditSllLesson as EditSllLessonType, UserRoleEnum} from '@src/types';
import { useRolesPermissions } from '@hooks/useRolesPermission';
import DeleteModal from '@src/components/modal/delete-modal/DeleteModal';
import { gradeLabelPositionOptions } from '@constants';
import { useToast } from '@src/hooks/useToast';
import { logger } from '../../../../../utils/logger';
import { useEditLesson } from '../../useEditLesson';

export type EditSllLesson = EditSllLessonType;

type Props = {
  sllLesson?: EditSllLesson;
  onSave: (p: EditSllLesson) => Promise<void>;
  loading: boolean;
  setLoading: (state: boolean) => void;
};

const defaultSllLesson: EditSllLesson = {
  name: '',
  duration: 30, // Mins
  country: '',
};

function EditLesson(props: Props) {
  const { onSave, loading, sllLesson, setLoading } = props;

  const [getUrl, { loading: urlLoading }] = useGetUrlLazyQuery({
    fetchPolicy: 'network-only',
  });

  const [isUnitUnique] = useIsValidSllUniqueNameLazyQuery({ fetchPolicy: 'network-only'});

  const history = useHistory();
  const { successToast, errorToast } = useToast();
    const { lesson } = useEditLesson();

  const [lessonContent, setLessonContent] = useState<EditSllLesson | undefined>();
  const [unitErrors, setUnitErrors] = useState<Partial<Record<ValidationErrorKeys, any>>>(validationErrors);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const isContentAdmin = useRolesPermissions([UserRoleEnum.ContentAdmin]);

  const [fileUploadingPercent, setFileUploadingPercent] = useState(0);
  const isEdit = useMemo(() => !!sllLesson, [sllLesson]);

  const [sllGetLessons, {data: sllLessons, loading: lessonLoading}] = useSllGetLessonsForSaLazyQuery({
    variables: { input: { keyword: '' } }, fetchPolicy: 'network-only'});

  const lessonlist = useMemo(() => {
    return (sllLessons?.sllGetLessonsForSA?.data || []).map(lesson => {
      return {
        label: lesson.name,
        value: lesson.id
      }
    })
  }, [sllLessons?.sllGetLessonsForSA]);

  useEffect(() => {
    sllGetLessons();
  }, [sllGetLessons])

  useEffect(() => {
    if (sllLesson) {
      if (sllLesson.__typename === 'SLLLesson') {
        setLessonContent({ ...sllLesson });
      }
    } else if (!lessonContent) {
      setLessonContent(defaultSllLesson);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sllLesson]);

  const [sllDeleteLesson, { loading: removeLoading }] = useSllDeleteLessonMutation({
    onCompleted() {
      setShowDeleteModal(false);
      successToast('Lesson is deleted successfully!');
      history.push('/super/lessons');
    },
  });

  const validate = useCallback((content?: EditSllLesson, isEdit?: boolean) => {
    const errors: Partial<Record<ValidationErrorKeys, string>> = {};

    if (validateIsEmpty(content?.name)) {
      errors.name = 'Please add a name.';
      errorToast('Please add a name.');
    }
    if ((!content?.competency || validateIsEmpty(content?.competency)) && content?.lessonType !== "7" && content?.lessonType !== "5" ) {
      errors.competency = 'Please add a Competency.';
      errorToast('Please add a Competency.');
    }
    if (!content?.overviewAnswers2?.length || content?.overviewAnswers2 === '[""]' || content?.overviewAnswers2 === '"[]"') {
      errors.overviewAnswers2 = 'Please add an item.';
      errorToast('Please add a Lesson Objectives.');
    }
    if (isEdit) {
      if (content?.lessonType === '5' && (!lesson?.materials || !lesson?.materials.find(m => m?.type === ContentType.PDF))) {
        errors.name = '';
        errorToast('This lesson cannot be saved as a Resource lesson type.');
      }
    } else {
      if (content?.lessonType === '5') {
        errors.name = '';
        errorToast('Cannot save lesson as Resource. Please use another lesson type to create lesson first.');
      }
    }
    return errors;
  }, [errorToast, lesson]);

  const getValidUnitContent = useCallback((content?: EditSllLesson) => {
    if (!content) return content;
    return ({
      ...content,
      lessonMaterialHeading: content.lessonMaterialHeading || 'View Lesson Preparation Materials:',
      overviewHeading1: content.overviewHeading1 || 'This Lesson teaches skills for:',
      overviewHeading2: content.overviewHeading2 || 'This Lesson will teach:',
      country: content.country || 'All Countries',
      gradeLevel: content.country !== 'Australia' ? content.gradeLevel : undefined,
      gradeLabelPosition: content.gradeLabelPosition || gradeLabelPositionOptions[2].value,
      comingSoonLabelPosition: content.comingSoonLabelPosition || gradeLabelPositionOptions[3].value,
      duration: parseInt(`${content.duration || 0}`),
      comingSoonAt: content.comingSoonAt ? content.comingSoonAt : undefined,
      lessonType: content.lessonType ? content.lessonType : "0",
    });
  }, []);

  const saveContent = useCallback(async (content: EditSllLesson, setError: Dispatch<SetStateAction<Partial<Record<ValidationErrorKeys, any>>>>) => {
    setError({});
    const errors = validate(content, true);

    if (isEmptyObject(errors)) {
      const newLessonContent = getValidUnitContent(content) as EditSllLesson;
      if (newLessonContent) {
        setLoading(true);

        // check if pretty url is valid
        if (content?.uniqueName) {
          const { data } = (await isUnitUnique({
            variables: {
              input: {
                id: content.id,
                uniqueName: content.uniqueName
              }
            }
          }));

          if (!data?.isValidSLLUniqueName) {
            errors.uniqueName = `Same pretty url name exists.`;
            setError(errors);
            setLoading(false);
            return;
          }
        }
        await onSave(newLessonContent);
      }
    } else {
      setError(errors);
    }
  }, [getValidUnitContent, isUnitUnique, onSave, setLoading, validate]);

  const handleSave = useCallback(async () => {
    if (!isEdit) {
      setUnitErrors({});
      const errors = validate(lessonContent, false);
      if (isEmptyObject(errors)) {
        if (lessonContent) {
          setLoading(true);
          const newLessonContent = getValidUnitContent(lessonContent);
          if (newLessonContent) {
            await onSave(newLessonContent);
          }
        }
      } else {
        setUnitErrors(errors);
      }
    } else if (lessonContent?.id) {
      setUnitErrors({});
      const errors = validate(lessonContent, true);
      if (isEmptyObject(errors)) {
        const newLessonContent = getValidUnitContent(lessonContent);
        if (newLessonContent) {
          await saveContent(newLessonContent, setUnitErrors);
        }
      } else {
        setUnitErrors(errors);
      }
    }
  }, [getValidUnitContent, isEdit, onSave, saveContent, setLoading, lessonContent, validate]);

  const handleCancel = () => {
    history.replace('/super/lessons')
  };

  const handleOpenModal = useCallback(() => setShowDeleteModal(true), []);
  const handleCloseModal = useCallback(() => {
    setShowDeleteModal(false)
  }, []);

  const handleDelete = useCallback(async () => {
    if (sllLesson?.id) {
      await sllDeleteLesson({
        variables: {
          input: { id: sllLesson?.id },
        },
      });
    }
  }, [sllDeleteLesson, sllLesson?.id]);

  const onChangeLessonContent = useCallback(async (content: Partial<EditSllLesson>) => {
    try {
      setLessonContent(data => {
        const newLessonContent: EditSllLesson = { ...data, ...content } as EditSllLesson;
        if (content.coverVideo || content.coverImage || content.bannerImage) {
          setUnitErrors({});
          const errors = validate(newLessonContent);
          if (isEmptyObject(errors)) {
            const newLessonContentValid = getValidUnitContent(newLessonContent);
            if (newLessonContentValid) {
              saveContent(newLessonContentValid, setUnitErrors);
            }
          } else {
            setUnitErrors(errors);
          }
        }
        return newLessonContent;
      });
    } catch (error) {
      // Handle any errors that might occur during file upload
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      errorToast(`File upload error: ${errorMessage}`);
      logger.error('Error in onChangeLessonContent:', error);
    }
  }, [getValidUnitContent, saveContent, validate, errorToast]);

  return (
    <>
      <div>
        <div className={styles.row}>
          <div className={styles.actions}>
            <div className={styles.title}>
              {isEdit ? 'Lesson Admin' : 'Create Lesson'}
            </div>
            <div className={styles.buttons}>
              <Button
                className={styles.saveButton} disabled={loading || urlLoading} isLoading={loading || urlLoading}
                leftIcon={loading || urlLoading ? <Spinner width={20} height={20} /> : undefined}
                onClick={handleSave} size={'small'} label={'SAVE'}
              />
              <Button className={styles.cancelButton} disabled={loading || urlLoading} onClick={handleCancel} size={'small'} label={'Cancel'}/>
            </div>
          </div>
        </div>
        {lessonContent ? (
          <EditLessonForm
            lesson={lessonContent}
            onChange={onChangeLessonContent}
            getUrl={getUrl}
            setLoading={setLoading}
            lessonLoading={lessonLoading}
            lessonlist={lessonlist}
            fileUploadingPercent={fileUploadingPercent}
            setFileUploadingPercent={setFileUploadingPercent}
            setErrors={setUnitErrors}
            errors={unitErrors}
          />
        ) : null}
        {isEdit ? (
          <div className={styles.deleteRow}>
            <hr className={styles.horizDivider} />
            <div className={styles.subTitle}>Warning</div>
            <Button className={styles.deleteButton} disabled={loading || urlLoading || isContentAdmin} onClick={handleOpenModal} size={'small'} label={'DELETE LESSON'}/>
          </div>
        ) : null}
      </div>
      {showDeleteModal && <DeleteModal open={!!showDeleteModal} loading={removeLoading || loading || urlLoading} onClose={handleCloseModal} onDelete={handleDelete} message={"Do you really want to Delete Lesson?"}/>}
    </>
  );
}

export default EditLesson;
